<script setup lang="ts">
import { useAllStores } from '@/store'
import { useClipboard } from '@vueuse/core'
import { handleError } from '@/utils/errors'
import { computed } from 'vue'

const { organizationStore, alertStore, t, router, authStore } = useAllStores()

const location = window.location

const organization_id = organizationStore.organization.id
const currentOrganization = organization_id.replace(/-/g, '')
const command = `curl https://${location.host}/install/${currentOrganization} | sudo bash`
const { copy, isSupported } = useClipboard({ source: command })

const { data, error } = await organizationStore.currentOrganization()
await handleError(error, router, authStore, alertStore)

const upgradeRequired = computed(() => {
  return data.value.plan.used_servers >= data.value.plan.max_servers
})

async function upgrade() {
  await router.push({ name: 'organizations:billing:plans', params: { organization_id } })
}
</script>

<i18n lang="yaml">
en:
  register: Register a server
  upgrade: You have reached the maximum number of servers for your plan
  change: Plan can be changed by clicking this alert message
  lead_msg: To register a server and manage its users and keys, connect to it via SSH and run
  info_msg: This command can be used for all servers you want to register. Once you have run the command, the server will be registered in AccessHub and you will be able to manage it from here.
  support_arch: Supported architectures
  step_header: 'What it does, step by step:'
  step1: Installs the AccessHub agent
  step2: Registers the server in your AccessHub organization
  step3: Imports all of the users and their keys
  source: source code
ru:
  register: Зарегистрировать сервер
  upgrade: Условия вашего тарифа не позволяют добавить больше серверов
  change: Тариф можно изменить щёлкнув по этому сообщению
  lead_msg: Чтобы зарегистрировать сервер и управлять доступом к нему, подключитесь по SSH и выполните
  info_msg: Эта команда может быть использована для всех серверов, которые вы хотите зарегистрировать. После выполнения команды сервер будет зарегистрирован в AccessHub и вы сможете управлять им отсюда.
  support_arch: Поддерживаемые архитектуры
  step_header: 'Вот что делает команда, шаг за шагом:'
  step1: Устанавливает агент AccessHub
  step2: Регистрирует сервер в вашей организации AccessHub
  step3: Импортирует всех пользователей и их ключи
  source: исходный код
</i18n>

<template>
  <div v-if="upgradeRequired">
    <div @click="upgrade" class="card alert alert-danger" role="alert">
      <h4 class="alert-heading">{{ t('upgrade') }}</h4>
      <p></p>
      <hr />
      <p class="mb-0">{{ t('change') }}</p>
    </div>
  </div>
  <div v-else>
    <h1>{{ t('register') }}</h1>
    <p class>{{ t('support_arch') }}: <kbd>x86_64</kbd> <kbd>aarch64</kbd></p>
    <p class="lead">{{ t('lead_msg') }}:</p>
    <p class="mt-3">
      <code class="align-middle">{{ command }}</code>
      <button ref="clipboardBtn" @click="copy(command)" class="btn ms-1" v-if="isSupported">
        <img src="@/assets/copy.svg" />
      </button>
    </p>

    <p class="lead">{{ t('info_msg') }}</p>

    <div class="lead">
      {{ t('step_header') }}
      <ol>
        <li>
          {{ t('step1') }}
          <a href="https://github.com/vmax/accesshub-agent">
            <img class="align-middle" src="@/assets/github.svg" />
            {{t('source')}}
          </a>
        </li>
        <li>{{ t('step2') }}</li>
        <li>{{ t('step3') }}</li>
      </ol>
    </div>
  </div>
</template>

<style scoped>
.btn {
  --bs-btn-hover-bg: transparent;
}

.card {
  transition:
    0.3s transform cubic-bezier(0.155, 1.105, 0.295, 1.12),
    0.3s box-shadow,
    0.3s -webkit-transform cubic-bezier(0.155, 1.105, 0.295, 1.12);
  cursor: pointer;
}

.card:hover {
  transform: scale(1.02);
  box-shadow:
    0 10px 20px rgba(0, 0, 0, 0.12),
    0 4px 8px rgba(0, 0, 0, 0.06);
}
</style>
